import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ViewerModeService {
  private _isLoading = false;
  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
  }

  private _isSplitScreen = false;
  get isSplitScreen(): boolean {
    return this._isSplitScreen;
  }
  set isSplitScreen(value: boolean) {
    if (value) this.isSplitScreenOpened = true;
    this._isSplitScreen = value;
  }

  private _isSplitScreenOpened = false;
  get isSplitScreenOpened(): boolean {
    return this._isSplitScreenOpened;
  }
  private set isSplitScreenOpened(value: boolean) {
    this._isSplitScreenOpened = value;
  }
  private _isMapVisible = true;
  get isMapVisible(): boolean {
    return this._isMapVisible;
  }
  set isMapVisible(value: boolean) {
    this._isMapVisible = value;
  }

  private _isObservationsVisible = true;
  get isObservationsVisible(): boolean {
    return this._isObservationsVisible;
  }
  set isObservationsVisible(value: boolean) {
    this._isObservationsVisible = value;
  }

  private _isBimSplit = false;
  get isBimSplit(): boolean {
    return this._isBimSplit;
  }
  set isBimSplit(value: boolean) {
    this._isBimSplit = value;
  }

  private _isBimLocked = false;
  get isBimLocked(): boolean {
    return this._isBimLocked;
  }
  set isBimLocked(value: boolean) {
    this._isBimLocked = value;
    this._isBimLocked$.next(value);
  }

  private readonly _isBimSplitBootstrapping$ = new BehaviorSubject<boolean>(false);
  get isBimSplitBootstrapping(): boolean {
    return this._isBimSplitBootstrapping$.value;
  }
  set isBimSplitBootstrapping(value: boolean) {
    this._isBimSplitBootstrapping$.next(value);
  }
  get isBimSplitBootstrapping$() {
    return this._isBimSplitBootstrapping$.asObservable();
  }

  // Observable for BIM lock state
  private readonly _isBimLocked$ = new BehaviorSubject<boolean>(false);
  get isBimLocked$() {
    return this._isBimLocked$.asObservable();
  }

  private _isPointCloud = false;
  get isPointCloud(): boolean {
    return this._isPointCloud;
  }
  set isPointCloud(value: boolean) {
    this._isPointCloud = value;
  }

  private _addObservationMode = false;
  get addObservationMode(): boolean {
    return this._addObservationMode;
  }
  set addObservationMode(value: boolean) {
    this._addObservationMode = value;
  }

  // Timelapse mode properties
  private _isTimelapse = false;
  get isTimelapse(): boolean {
    return this._isTimelapse;
  }
  set isTimelapse(value: boolean) {
    this._isTimelapse = value;
    // When entering timelapse mode, hide other UI elements
    if (value) {
      this._previousStates = {
        isMapVisible: this._isMapVisible,
        isObservationsVisible: this._isObservationsVisible,
        isSplitScreen: this._isSplitScreen,
        isBimSplit: this._isBimSplit,
        isPointCloud: this._isPointCloud,
        addObservationMode: this._addObservationMode,
      };
      // Hide unnecessary UI elements in timelapse mode
      this._isMapVisible = false;
      this._isSplitScreen = false;
      this._isBimSplit = false;
      this._isPointCloud = false;
      this._addObservationMode = false;
    }
  }

  // Observable for reactive updates (optional, for components that need it)
  private readonly _isTimelapse$ = new BehaviorSubject<boolean>(false);
  get isTimelapse$() {
    return this._isTimelapse$.asObservable();
  }

  // Store previous states to restore when exiting timelapse mode
  private _previousStates: any = {};

  /**
   * Activate timelapse mode
   */
  activateTimelapse(): void {
    this.isTimelapse = true;
    this._isTimelapse$.next(true);
  }

  /**
   * Deactivate timelapse mode and restore previous states
   */
  deactivateTimelapse(): void {
    this.isTimelapse = false;
    this._isTimelapse$.next(false);

    // Restore previous states if they exist
    if (this._previousStates) {
      this._isMapVisible = this._previousStates.isMapVisible ?? true;
      this._isObservationsVisible = this._previousStates.isObservationsVisible ?? true;
      this._isSplitScreen = this._previousStates.isSplitScreen ?? false;
      this._isBimSplit = this._previousStates.isBimSplit ?? false;
      this._isPointCloud = this._previousStates.isPointCloud ?? false;
      this._addObservationMode = this._previousStates.addObservationMode ?? false;
      this._previousStates = {};
    }
  }

  togglePointCloud() {
    this._isPointCloud = !this._isPointCloud;
  }

  toggleSplitScreen() {
    this.isSplitScreen = !this.isSplitScreen;
  }

  toggleBimSplit() {
    this.isBimSplit = !this.isBimSplit;
  }

  toggleBimLock() {
    this.isBimLocked = !this.isBimLocked;
  }

  toggleMapVisibility() {
    this.isMapVisible = !this.isMapVisible;
  }
  toggleObservationVisibilty() {
    this.isObservationsVisible = !this.isObservationsVisible;
  }
  toggleAddObservationMode() {
    this.addObservationMode = !this.addObservationMode;
  }

  reset() {
    this.isLoading = false;
    this.isSplitScreen = false;
    this.isMapVisible = true;
    this.isBimSplit = false;
    this.isPointCloud = false;
    this.addObservationMode = false;
    this.isBimLocked = false;
    this.isSplitScreenOpened = false;
    this.isTimelapse = false;
    this._isTimelapse$.next(false);
    this._previousStates = {};
    this._isBimSplitBootstrapping$.next(false);
  }
}
